import { defineMessages, type MessageDescriptor } from 'react-intl';
import type { CoverLeadError } from './coverLead';

/** The copy of the public cover maker (design cover-editor g). */
export const publicCoverMessages = defineMessages({
  brand: { id: 'app.sml.coverEditor.public.brand', defaultMessage: 'Song artwork maker' },
  titleLabel: { id: 'app.sml.coverEditor.public.field.title', defaultMessage: 'Song title' },
  artistLabel: { id: 'app.sml.coverEditor.public.field.artist', defaultMessage: 'Artist name' },
  kept: { id: 'app.sml.coverEditor.public.kept', defaultMessage: 'Kept in this browser' },
  logIn: { id: 'app.sml.coverEditor.public.logIn', defaultMessage: 'Log in' },
  signUp: { id: 'app.sml.coverEditor.public.signUp', defaultMessage: 'Sign up free' },
  loadFailed: {
    id: 'app.sml.coverEditor.public.loadFailed',
    defaultMessage: 'This design link does not work. We opened a new design.',
  },
  uploadLost: {
    id: 'app.sml.coverEditor.public.uploadLost',
    defaultMessage: 'Your own picture was not kept by this browser. Add it again in Picture.',
  },
  dismiss: { id: 'app.sml.coverEditor.public.dismiss', defaultMessage: 'OK' },
  uploadLocalHint: {
    id: 'app.sml.coverEditor.public.upload.hint',
    defaultMessage: 'Your picture stays in this browser. We do not upload it.',
  },

  // The AI tab: the upsell card (g2)
  aiEyebrow: { id: 'app.sml.coverEditor.public.ai.eyebrow', defaultMessage: 'With a paid plan' },
  aiTitle: { id: 'app.sml.coverEditor.public.ai.title', defaultMessage: 'AI pictures made for your song.' },
  aiText: {
    id: 'app.sml.coverEditor.public.ai.text',
    defaultMessage:
      'Say what you see and get 4 pictures that fit the title, the lyrics and the mood of your song. No credits.',
  },
  aiPlans: { id: 'app.sml.coverEditor.public.ai.plans', defaultMessage: 'See the plans' },
  aiSignUp: { id: 'app.sml.coverEditor.public.ai.signUp', defaultMessage: 'Sign up free first' },
  aiFree: { id: 'app.sml.coverEditor.public.ai.free', defaultMessage: 'Free in this tool' },
  aiFreeText: {
    id: 'app.sml.coverEditor.public.ai.freeText',
    defaultMessage:
      'The picture bank, your own picture, colours, text, fonts and effects are free. The download carries the MUSIC24 watermark; a paid plan removes it.',
  },
  aiOpenBank: { id: 'app.sml.coverEditor.public.ai.openBank', defaultMessage: 'Open the bank' },

  // The keep card of the inspector (g1)
  keepTitle: { id: 'app.sml.coverEditor.public.keep.title', defaultMessage: 'Keep this design.' },
  keepText: {
    id: 'app.sml.coverEditor.public.keep.text',
    defaultMessage: 'Sign up free and make it the cover of your song. Your design comes with you.',
  },

  // The email dialog (g3)
  emailTitle: { id: 'app.sml.coverEditor.public.email.title', defaultMessage: 'Where do we send your design?' },
  emailText: {
    id: 'app.sml.coverEditor.public.email.text',
    defaultMessage: 'Leave your email and the download starts.',
  },
  emailLabel: { id: 'app.sml.coverEditor.public.email.label', defaultMessage: 'Your email' },
  emailPlaceholder: { id: 'app.sml.coverEditor.public.email.placeholder', defaultMessage: 'you@example.com' },
  emailWhat: {
    id: 'app.sml.coverEditor.public.email.what',
    defaultMessage:
      'We save your design and send you a link to it. We also send you tips about MUSIC24. You can unsubscribe at any time.',
  },
  emailFile: {
    id: 'app.sml.coverEditor.public.email.file',
    defaultMessage: '{size} × {size} px JPEG · with the MUSIC24 watermark',
  },
  emailConfirm: { id: 'app.sml.coverEditor.public.email.confirm', defaultMessage: 'Download' },
  emailSaving: { id: 'app.sml.coverEditor.public.email.saving', defaultMessage: 'Saving your design…' },
  emailCancel: { id: 'app.sml.coverEditor.public.email.cancel', defaultMessage: 'Cancel' },
  downloadFailed: {
    id: 'app.sml.coverEditor.public.downloadFailed',
    defaultMessage: 'The file could not be made in this browser. Try again.',
  },

  // After the download (g4)
  doneTitle: { id: 'app.sml.coverEditor.public.done.title', defaultMessage: 'Your download started.' },
  doneSent: {
    id: 'app.sml.coverEditor.public.done.sent',
    defaultMessage: 'We sent a link to your design to {email}.',
  },
  doneLater: {
    id: 'app.sml.coverEditor.public.done.later',
    defaultMessage: 'Your design is kept in this browser.',
  },
  doneStepCarry: {
    id: 'app.sml.coverEditor.public.done.step.carry',
    defaultMessage: 'Your design comes with you. It opens in the editor of your song.',
  },
  doneStepUse: {
    id: 'app.sml.coverEditor.public.done.step.use',
    defaultMessage: 'Use it as the cover. MUSIC24 makes the posts and the videos of the release from it.',
  },
  doneStepPaid: {
    id: 'app.sml.coverEditor.public.done.step.paid',
    defaultMessage: 'A paid plan removes the watermark and makes AI pictures for your song.',
  },
  doneConfirm: { id: 'app.sml.coverEditor.public.done.confirm', defaultMessage: 'Continue in MUSIC24' },
  doneHint: { id: 'app.sml.coverEditor.public.done.hint', defaultMessage: 'With this email. No card.' },
  doneStay: { id: 'app.sml.coverEditor.public.done.stay', defaultMessage: 'Keep editing' },
});

/** The line of each refusal of the email dialog (g3). `down` shows no line: the download starts. */
export const coverLeadErrorMessages: Record<Exclude<CoverLeadError, 'down'>, MessageDescriptor> = defineMessages({
  invalid: {
    id: 'app.sml.coverEditor.public.email.invalid',
    defaultMessage: 'This email does not look right. Check it and try again.',
  },
  disposable: {
    id: 'app.sml.coverEditor.public.email.disposable',
    defaultMessage: 'Use another email address. This one is a temporary address.',
  },
  rate: {
    id: 'app.sml.coverEditor.public.email.rate',
    defaultMessage:
      'Too many downloads from this network. Try again in an hour. Your design is still here, in this browser.',
  },
  failed: {
    id: 'app.sml.coverEditor.public.email.failed',
    defaultMessage: 'We could not save your design. Try again.',
  },
});
